import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Card, Button, Modal, Form, ProgressBar } from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";

// Actions de Redux
import { obtenerPedidosAction } from "../../../../redux/actions/pedidosActions";
import { obtenerRutasAction } from "../../../../redux/actions/rutasActions";
import { obtenerWsgStatusAction } from "../../../../redux/actions/wsgStatusActions";
import {
  editarPedidoRutaAction,
  resetEditarPedidoRutaAction,
} from "../../../../redux/actions/pedidoRutaActions";

// Componentes
import PedidoCard from "../PedidoCard";
import { Spinner, Alert } from "../../../Utilidades";

import Swal from "sweetalert2";

import constantes from "../../../../lib/constantes";

import imageCompression from "browser-image-compression";

const BotonesFlotantes = ({ setShowSearch }) => {
  return (
    <>
      {/* ⬆️ Botón flotante para ir al inicio */}
      <button
        className="btn btn-secondary position-fixed rounded-circle"
        style={{ bottom: "1rem", right: "1rem", zIndex: 3001 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <i className="bi bi-arrow-up fs-4"></i>
      </button>

      {/* 🔍 Botón flotante para mostrar la caja de búsqueda */}
      <button
        className="btn btn-primary position-fixed rounded-circle"
        style={{ bottom: "4.5rem", right: "1rem", zIndex: 3001 }}
        onClick={() => {
          setShowSearch((prev) => !prev);
        }}
      >
        <i className="bi bi-search fs-4"></i>
      </button>
    </>
  );
};

const ModalNoEntregado = ({
  pedido,
  show,
  onHide,
  wsgstatus,
  subiendo,
  setSubiendo,
}) => {
  const dispatch = useDispatch();
  const { identifier } = pedido;
  const formikRefNEN = useRef(null);
  const fotosInputRef = useRef(null);
  const galeriaInputRef = useRef(null);
  const [razonNoEntrega, setRazonNoEntrega] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [fotos, setFotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [cargandoFotos, setCargandoFotos] = useState(false);

  const { id: rutasGuiasId } = pedido;

  const { user } = useSelector((state) => state.login);

  useEffect(() => {
    if (!show) {
      previews.forEach((url) => URL.revokeObjectURL(url));
      setFotos([]);
      setPreviews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const eliminarFoto = (index) => {
    URL.revokeObjectURL(previews[index]);
    const nuevasFotos = fotos.filter((_, i) => i !== index);
    const nuevasPreviews = previews.filter((_, i) => i !== index);
    setFotos(nuevasFotos);
    setPreviews(nuevasPreviews);
    formikRefNEN.current.setFieldValue("fotos", nuevasFotos);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 2, // Máximo 2MB por imagen
      maxWidthOrHeight: 1024, // Redimensionar a máximo 1024px
      useWebWorker: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error al comprimir imagen:", error);
      return file; // Si falla la compresión, enviar original
    }
  };

  const handleFotosChange = async (e) => {
    const filesArray = Array.from(e.target.files);
    if (filesArray.length === 0) return;

    const validFiles = filesArray.filter((file) =>
      ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)
    );

    if (validFiles.length !== filesArray.length) {
      Swal.fire({
        icon: "error",
        title: "Archivo inválido",
        text: "Solo se permiten imágenes (jpg, jpeg, png, gif, webp)",
      });
      e.target.value = "";
      return;
    }

    try {
      setCargandoFotos(true);
      const compressedFiles = await Promise.all(
        validFiles.map((file) => compressImage(file))
      );

      const nuevasFotos = [...fotos, ...compressedFiles];
      const nuevasPreviews = [
        ...previews,
        ...compressedFiles.map((f) => URL.createObjectURL(f)),
      ];
      setFotos(nuevasFotos);
      setPreviews(nuevasPreviews);
      formikRefNEN.current.setFieldValue("fotos", nuevasFotos);
    } catch (error) {
      console.error("Error al comprimir imágenes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al comprimir las imágenes.",
      });
    } finally {
      setCargandoFotos(false);
      e.target.value = "";
    }
  };

  const noEntregadoSchema = Yup.object().shape({
    noEntregaList: Yup.string().required("Requerido"),
    observaciones: Yup.string()
      .min(2, "Mínimo 2 caracteres")
      .max(250, "Máximo 250 caracteres"),
  });

  const subStatusNEN = wsgstatus.find(
    (estado) => estado.codigo === constantes.WSG_STATUS.NO_ENTREGADO
  );

  const subStatusesNEN =
    subStatusNEN && subStatusNEN.wsg_substatuses.length > 0
      ? subStatusNEN.wsg_substatuses.map((sub) => sub.nombre)
      : [];

  // Confirmar si desea guardar los cambios
  const confirmarModalNEN = (form) => {
    // preguntar al usuario
    Swal.fire({
      title: "¿Está seguro que desea guardar?",
      text: "",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        setSubiendo(true);
        const formData = new FormData();
        formData.append("identifier", identifier);
        formData.append("rutasGuiasId", rutasGuiasId);
        formData.append("usuarios_id_entrega", user.usuario.uid);
        formData.append("wsg_status", constantes.WSG_STATUS.NO_ENTREGADO);
        formData.append("status", subStatusNEN.nombre);
        formData.append("substatus", form.noEntregaList);
        formData.append("observaciones", form.observaciones);
        fotos.forEach((foto) => {
          formData.append("file", foto);
        });
        dispatch(editarPedidoRutaAction(formData));
      }
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{identifier}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          innerRef={formikRefNEN}
          initialValues={{
            noEntregaList: razonNoEntrega,
            observaciones: "",
            fotos: [],
          }}
          enableReinitialize
          validationSchema={noEntregadoSchema}
          onSubmit={(values, actions) => {
            confirmarModalNEN(values);
          }}
        >
          {({ errors, touched, values, handleChange }) => (
            <FormikForm>
              <Form.Group className="mb-2">
                <Form.Select
                  id="noEntregaList"
                  name="noEntregaList"
                  value={values.noEntregaList}
                  onChange={(e) => {
                    handleChange(e);
                    setRazonNoEntrega(e.target.value); // seguir actualizando el state
                  }}
                  isInvalid={touched.noEntregaList && !!errors.noEntregaList}
                >
                  <option value="">Selecciona la razón de no entrega</option>
                  {subStatusesNEN.map((nombre, i) => (
                    <option key={i} value={nombre}>
                      {nombre}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.noEntregaList}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea"
                  id="observaciones"
                  name="observaciones"
                  rows={1}
                  value={observaciones}
                  onChange={(e) => {
                    handleChange(e);
                    setObservaciones(e.target.value);
                  }}
                  placeholder="Observaciones (opcional)"
                  isInvalid={touched.observaciones && !!errors.observaciones}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.observaciones}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <input
                  ref={fotosInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                  onChange={handleFotosChange}
                />
                <input
                  ref={galeriaInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                  onChange={handleFotosChange}
                />
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => fotosInputRef.current?.click()}
                    disabled={cargandoFotos}
                  >
                    {cargandoFotos ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Comprimiendo...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-camera me-1"></i>
                        Tomar foto (opcional)
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => galeriaInputRef.current?.click()}
                    disabled={cargandoFotos}
                  >
                    <i className="bi bi-images me-1"></i>
                    Galería
                  </Button>
                  {fotos.length > 0 && (
                    <small className="text-muted">
                      {fotos.length} foto{fotos.length !== 1 ? "s" : ""}
                    </small>
                  )}
                </div>
                {previews.length > 0 && (
                  <div
                    className="d-flex flex-wrap gap-2"
                    style={{ marginTop: "14px" }}
                  >
                    {previews.map((src, i) => (
                      <div key={i} className="position-relative">
                        <img
                          src={src}
                          alt={`Foto ${i + 1}`}
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => eliminarFoto(i)}
                          className="position-absolute top-0 end-0 btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "20px",
                            height: "20px",
                            fontSize: "12px",
                            transform: "translate(50%, -50%)",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
            </FormikForm>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={subiendo}>
          Salir
        </Button>
        {cargandoFotos && (
          <Button variant="primary" disabled>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Comprimiendo...
          </Button>
        )}
        {!cargandoFotos && (
          <Button
            variant="primary"
            onClick={() => {
              formikRefNEN.current?.submitForm();
            }}
            disabled={subiendo}
          >
            {subiendo ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const ModalEntregado = ({
  pedido,
  show,
  onHide,
  wsgstatus,
  subiendo,
  setSubiendo,
}) => {
  const dispatch = useDispatch();
  const { identifier } = pedido;
  const formikRefENT = useRef(null);
  const fotosInputRef = useRef(null);
  const galeriaInputRef = useRef(null);
  const [paso, setPaso] = useState(1);
  const [fotos, setFotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [cargandoFotos, setCargandoFotos] = useState(false);

  const { id: rutasGuiasId } = pedido;
  const { user } = useSelector((state) => state.login);

  useEffect(() => {
    if (!show) {
      previews.forEach((url) => URL.revokeObjectURL(url));
      setPaso(1);
      setFotos([]);
      setPreviews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const eliminarFoto = (index) => {
    URL.revokeObjectURL(previews[index]);
    const nuevasFotos = fotos.filter((_, i) => i !== index);
    const nuevasPreviews = previews.filter((_, i) => i !== index);
    setFotos(nuevasFotos);
    setPreviews(nuevasPreviews);
    formikRefENT.current.setFieldValue("fotos", nuevasFotos);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 2, // Máximo 2MB por imagen
      maxWidthOrHeight: 1024, // Redimensionar a máximo 1024px
      useWebWorker: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error al comprimir imagen:", error);
      return file; // Si falla la compresión, enviar original
    }
  };

  const handleFotosChange = async (e) => {
    const filesArray = Array.from(e.target.files);
    if (filesArray.length === 0) return;

    const validFiles = filesArray.filter((file) =>
      ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)
    );

    if (validFiles.length !== filesArray.length) {
      Swal.fire({
        icon: "error",
        title: "Archivo inválido",
        text: "Solo se permiten imágenes (jpg, jpeg, png, gif, webp)",
      });
      e.target.value = "";
      return;
    }

    try {
      setCargandoFotos(true);
      const compressedFiles = await Promise.all(
        validFiles.map((file) => compressImage(file))
      );

      const nuevasFotos = [...fotos, ...compressedFiles];
      const nuevasPreviews = [
        ...previews,
        ...compressedFiles.map((f) => URL.createObjectURL(f)),
      ];
      setFotos(nuevasFotos);
      setPreviews(nuevasPreviews);
      formikRefENT.current.setFieldValue("fotos", nuevasFotos);
    } catch (error) {
      console.error("Error al comprimir imágenes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al comprimir las imágenes.",
      });
    } finally {
      setCargandoFotos(false);
      e.target.value = "";
    }
  };

  const entregadoSchema = Yup.object().shape({
    entregaList: Yup.string().required("Requerido"),
    nombreRecibe: Yup.string()
      .min(2, "Mínimo 2 caracteres")
      .max(50, "Máximo 50 caracteres")
      .required("Requerido"),
    // rutRecibe: Yup.string()
    //   .min(2, "Mínimo 2 caracteres")
    //   .max(50, "Máximo 50 caracteres"),
    observaciones: Yup.string()
      .min(2, "Mínimo 2 caracteres")
      .max(250, "Máximo 250 caracteres"),
    fotos: Yup.array()
      .min(1, "Debe subir al menos una foto")
      .test(
        "fileFormat",
        "Solo se permiten imágenes (jpg, jpeg, png, gif, webp)",
        (files) =>
          files &&
          files.every((file) =>
            ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(
              file.type
            )
          )
      ),
  });

  const subStatusENT = wsgstatus.find(
    (estado) => estado.codigo === constantes.WSG_STATUS.ENTREGADO
  );

  const subStatusesENT =
    subStatusENT && subStatusENT.wsg_substatuses.length > 0
      ? subStatusENT.wsg_substatuses.map((sub) => sub.nombre)
      : [];

  const confirmarModalENT = (form) => {
    Swal.fire({
      title: "¿Está seguro que desea guardar?",
      text: "",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        setSubiendo(true);
        const formData = new FormData();
        formData.append("identifier", identifier);
        formData.append("rutasGuiasId", rutasGuiasId);
        formData.append("usuarios_id_entrega", user.usuario.uid);
        formData.append("wsg_status", constantes.WSG_STATUS.ENTREGADO);
        formData.append("status", subStatusENT.nombre);
        formData.append("substatus", form.entregaList);
        formData.append("receptor", form.nombreRecibe);
        //formData.append("rut_receptor", form.rutRecibe);
        formData.append("rut_receptor", "");
        formData.append("observaciones", form.observaciones);
        fotos.forEach((foto) => {
          formData.append("file", foto);
        });
        dispatch(editarPedidoRutaAction(formData));
      }
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{identifier}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          innerRef={formikRefENT}
          initialValues={{
            entregaList: "",
            nombreRecibe: "",
            //rutRecibe: "",
            observaciones: "",
            fotos: [],
          }}
          enableReinitialize
          validationSchema={entregadoSchema}
          onSubmit={(values) => {
            confirmarModalENT(values);
          }}
        >
          {({ errors, touched, values, handleChange }) => (
            <FormikForm>
              {paso === 1 && (
                <>
                  <Form.Group className="mb-3">
                    <input
                      ref={fotosInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                      onChange={handleFotosChange}
                    />
                    <input
                      ref={galeriaInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                      onChange={handleFotosChange}
                    />
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        onClick={() => fotosInputRef.current?.click()}
                        disabled={cargandoFotos}
                      >
                        {cargandoFotos ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Comprimiendo...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-camera me-1"></i>
                            Tomar foto
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        onClick={() => galeriaInputRef.current?.click()}
                        disabled={cargandoFotos}
                      >
                        <i className="bi bi-images me-1"></i>
                        Galería
                      </Button>
                      {fotos.length > 0 && (
                        <small className="text-muted">
                          {fotos.length} foto{fotos.length !== 1 ? "s" : ""}
                        </small>
                      )}
                    </div>
                    {previews.length > 0 && (
                      <div
                        className="d-flex flex-wrap gap-2"
                        style={{ marginTop: "14px" }}
                      >
                        {previews.map((src, i) => (
                          <div key={i} className="position-relative">
                            <img
                              src={src}
                              alt={`Foto ${i + 1}`}
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "1px solid #dee2e6",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => eliminarFoto(i)}
                              className="position-absolute top-0 end-0 btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
                              style={{
                                width: "20px",
                                height: "20px",
                                fontSize: "12px",
                                transform: "translate(50%, -50%)",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {touched.fotos && errors.fotos && (
                      <div className="invalid-feedback d-block mt-1">
                        {errors.fotos}
                      </div>
                    )}
                  </Form.Group>
                </>
              )}

              {paso === 2 && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Select
                      id="entregaList"
                      name="entregaList"
                      value={values.entregaList}
                      onChange={handleChange}
                      isInvalid={touched.entregaList && !!errors.entregaList}
                    >
                      <option value="">¿Dónde se entregó?</option>
                      {subStatusesENT.map((nombre, i) => (
                        <option key={i} value={nombre}>
                          {nombre}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.entregaList}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Control
                      id="nombreRecibe"
                      name="nombreRecibe"
                      type="text"
                      placeholder="Nombre quien recibe"
                      value={values.nombreRecibe}
                      onChange={handleChange}
                      isInvalid={touched.nombreRecibe && !!errors.nombreRecibe}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nombreRecibe}
                    </Form.Control.Feedback>
                  </Form.Group>
                  {/* <Form.Group className="mb-2">
                    <Form.Control
                      id="rutRecibe"
                      name="rutRecibe"
                      type="text"
                      placeholder="RUT quien recibe"
                      value={values.rutRecibe}
                      onChange={handleChange}
                      isInvalid={touched.rutRecibe && !!errors.rutRecibe}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.rutRecibe}
                    </Form.Control.Feedback>
                  </Form.Group> */}
                  <Form.Group className="mb-2">
                    <Form.Control
                      as="textarea"
                      id="observaciones"
                      name="observaciones"
                      rows={1}
                      placeholder="Observaciones (opcional)"
                      value={values.observaciones}
                      onChange={handleChange}
                      isInvalid={
                        touched.observaciones && !!errors.observaciones
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.observaciones}
                    </Form.Control.Feedback>
                  </Form.Group>
                </>
              )}
            </FormikForm>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer>
        {paso === 1 && (
          <>
            <Button
              variant="secondary"
              onClick={onHide}
              disabled={subiendo}
            >
              Salir
            </Button>
            <Button
              variant="primary"
              onClick={() => setPaso(2)}
              disabled={fotos.length === 0 || subiendo || cargandoFotos}
            >
              {cargandoFotos ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Comprimiendo...
                </>
              ) : (
                "Continuar"
              )}
            </Button>
          </>
        )}
        {paso === 2 && (
          <>
            <Button
              variant="secondary"
              onClick={() => setPaso(1)}
              disabled={subiendo}
            >
              Volver
            </Button>
            <Button
              variant="primary"
              onClick={() => formikRefENT.current?.submitForm()}
              disabled={subiendo}
            >
              {subiendo ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Guardando...
                </>
              ) : (
                "Finalizar"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const ModalVerEntrega = ({ pedido, show, onHide }) => {
  const {
    identifier,
    wsg_status: estadoActual,
    receptor,
    rut_receptor,
    observaciones,
    foto,
  } = pedido;

  const {
    shipping_first_name: first_name,
    shipping_last_name: last_name,
    shipping_address_1: address,
    shipping_address_2: complement,
    shipping_city: commune,
    shipping_phone: phone,
    shipping_state: region,
    customer_note: nota,
  } = pedido.vista_pedidos[0];

  const [showModalFoto, setShowModalFoto] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const handleThumbnailClick = (i) => {
    setCarouselIndex(i);
    setShowModalFoto(true);
  };

  const handleNext = () => {
    setCarouselIndex((prev) => (prev + 1) % foto.length);
  };

  const handlePrev = () => {
    setCarouselIndex((prev) => (prev - 1 + foto.length) % foto.length);
  };

  return (
    <>
      {/* Modal principal */}
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{identifier}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card.Text>
            <strong>
              {first_name} {last_name}
            </strong>
          </Card.Text>
          <Card.Text>
            Dirección: {address}, Dpto/Casa: {complement}, Comuna: {commune} -{" "}
            {region}
          </Card.Text>
          <Card.Text>
            <div className="rm-2">Teléfono: {phone}</div>
          </Card.Text>
          {nota && (
            <Card.Text>
              <div className="rm-2">Nota del cliente: {nota}</div>
            </Card.Text>
          )}
          {estadoActual === constantes.WSG_STATUS.ENTREGADO && (
            <>
              <Card.Text>
                <div className="rm-2">Nombre quien recibe: {receptor}</div>
              </Card.Text>
              {rut_receptor && (
                <Card.Text>
                  <div className="rm-2">RUT quien recibe: {rut_receptor}</div>
                </Card.Text>
              )}
            </>
          )}
          {observaciones && (
            <Card.Text>
              <div className="rm-2">Observaciones: {observaciones}</div>
            </Card.Text>
          )}
          {foto && foto.length > 0 && (
            <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
              {foto.map((foto, i) => (
                <img
                  key={i}
                  src={foto?.image}
                  alt={`Foto ${i + 1}`}
                  className="img-thumbnail"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                  onClick={() => handleThumbnailClick(i)}
                />
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between"></Modal.Footer>
      </Modal>

      {/* Modal secundario para carrusel de imágenes */}
      <Modal
        show={showModalFoto}
        onHide={() => setShowModalFoto(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{foto[carouselIndex]?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img
            key={foto[carouselIndex]}
            src={foto[carouselIndex]?.image}
            alt={`Foto-${carouselIndex}`}
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
            }}
          />
        </Modal.Body>
        {foto && foto?.length > 0 && (
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={handlePrev}
              disabled={carouselIndex === 0}
            >
              <i className="bi bi-arrow-left-circle-fill fs-5"></i>
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleNext}
              disabled={carouselIndex === (foto?.length || 0) - 1}
            >
              <i className="bi bi-arrow-right-circle-fill fs-5"></i>
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

const ListaPedidos = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [totalPedidosENT, setTotalPedidosENT] = useState(0);
  const [totalPedidosNEN, setTotalPedidosNEN] = useState(0);
  const [pedidosLocal, setPedidosLocal] = useState([]);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState();
  const [showFormularioModalENT, setShowFormularioModalENT] = useState(false);
  const [showFormularioModalNEN, setShowFormularioModalNEN] = useState(false);
  const [showVerEntregaModal, setShowVerEntregaModal] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [editable, setEditable] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const searchRef = useRef();

  const { id: rutasId } = params;

  const { user } = useSelector((state) => state.login);

  const {
    loading: loadingRutas,
    error: errorRutas,
    rutas,
  } = useSelector((state) => state.rutas);

  useEffect(() => {
    if (user?.usuario?.uid) {
      dispatch(obtenerRutasAction("1", user.usuario.uid));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (rutasId && rutas?.length > 0) {
      const rutaProcesar = rutas.find((ruta) => ruta.id === parseInt(rutasId));
      const { started, ended } = rutaProcesar || {};
      if (started && !ended) {
        setEditable(true);
      }
    }
  }, [rutas, rutasId]);

  const {
    pedidos,
    loading: loadingPedidos,
    error: errorPedidos,
  } = useSelector((state) => state.pedidos);

  const { wsgstatus } = useSelector((state) => state.wsgstatus);

  const {
    data: dataUpdatePedidoRuta,
    loading: loadingPedidoRuta,
    error: errorPedidoRuta,
    errorMessage: errorMessagePedidoRuta,
  } = useSelector((state) => state.pedidoruta);

  useEffect(() => {
    if (
      !errorPedidoRuta &&
      dataUpdatePedidoRuta &&
      dataUpdatePedidoRuta?.resultado
    ) {
      setShowFormularioModalENT(false);
      setShowFormularioModalNEN(false);
      dispatch(resetEditarPedidoRutaAction());
      setSubiendo(false);

      const {
        fecha_entrega,
        receptor,
        rut_receptor,
        wsg_status,
        status,
        substatus,
        observaciones,
        foto,
      } = dataUpdatePedidoRuta.pedido;

      setPedidosLocal((prev) =>
        prev.map((p) =>
          p.id === pedidoSeleccionado?.id
            ? {
                ...p,
                fecha_entrega,
                receptor,
                rut_receptor,
                wsg_status,
                status,
                substatus,
                observaciones,
                foto,
              }
            : p
        )
      );

      Swal.fire({
        icon: "success",
        title: dataUpdatePedidoRuta?.msg,
        showConfirmButton: false,
        timer: 2500,
      });
    }

    if (errorPedidoRuta) {
      Swal.fire({
        icon: "error",
        title: errorMessagePedidoRuta,
      });
      dispatch(resetEditarPedidoRutaAction());
    }
  }, [
    dataUpdatePedidoRuta,
    loadingPedidoRuta,
    errorPedidoRuta,
    errorMessagePedidoRuta,
    pedidoSeleccionado,
    dispatch,
  ]);

  useEffect(() => {
    if (!loadingRutas && !errorRutas && rutasId) {
      dispatch(obtenerPedidosAction(rutasId));
      dispatch(obtenerWsgStatusAction());
    }
  }, [loadingRutas, errorRutas, rutasId, dispatch]);

  const RutaProgreso = ({ total, entregados, noEntregados }) => {
    const pendientes = total - entregados - noEntregados;

    const percentEntregados = Math.round((entregados / total) * 100);
    const percentNoEntregados = Math.round((noEntregados / total) * 100);
    const percentPendientes = 100 - percentEntregados - percentNoEntregados;

    return (
      <div className="mt-3 mb-3 p-2 border rounded bg-light">
        <div className="mt-1 row text-center">
          <div className="col p-1">
            <strong>Total:</strong>
            <br />
            {total}
          </div>
          <div className="col text-success p-1">
            <strong>Entreg:</strong>
            <br />
            {entregados} ({percentEntregados}%)
          </div>
          <div className="col text-danger p-1">
            <strong>No Entreg:</strong>
            <br />
            {noEntregados} ({percentNoEntregados}%)
          </div>
          <div className="col text-muted p-1">
            <strong>Pend:</strong>
            <br />
            {pendientes} ({percentPendientes}%)
          </div>
        </div>

        <ProgressBar className="mt-3">
          <ProgressBar
            variant="success"
            now={percentEntregados}
            key={1}
            label={`${percentEntregados}%`}
          />
        </ProgressBar>
      </div>
    );
  };

  useEffect(() => {
    setPedidosLocal(pedidos);
  }, [pedidos]);

  useEffect(() => {
    const totalENT = pedidosLocal.filter(
      (p) => p.wsg_status === constantes.WSG_STATUS.ENTREGADO
    ).length;
    const totalNEN = pedidosLocal.filter(
      (p) => p.wsg_status === constantes.WSG_STATUS.NO_ENTREGADO
    ).length;
    setTotalPedidos(pedidosLocal.length);
    setTotalPedidosENT(totalENT);
    setTotalPedidosNEN(totalNEN);
  }, [pedidosLocal]);

  // Crear referencias para cada card
  const cardRefs = useRef([]);

  const handleEntrega = useCallback((pedido) => {
    setPedidoSeleccionado(pedido);
    setShowFormularioModalENT(true);
  }, []);

  const handleNoEntrega = useCallback((pedido) => {
    setPedidoSeleccionado(pedido);
    setShowFormularioModalNEN(true);
  }, []);

  const handleVerEntrega = useCallback((pedido) => {
    setPedidoSeleccionado(pedido);
    setShowVerEntregaModal(true);
  }, []);

  const resetValues = () => {
    setSubiendo(false);
    setPedidoSeleccionado(null);
  };

  // cerrar buscador al hacer clic fuera del input
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cerrar buscador al presionar ESC
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowSearch(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Scroll automático al primer resultado
  // useEffect(() => {
  //   if (searchTerm.trim() === "") return;

  //   if (pedidosLocal.length === 0) return;

  //   const firstMatchIndex = pedidosLocal.findIndex(
  //     (pedido) =>
  //       pedido.identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       pedido.shipping_first_name
  //         .toLowerCase()
  //         .includes(searchTerm.toLowerCase()) ||
  //       pedido.shipping_last_name
  //         .toLowerCase()
  //         .includes(searchTerm.toLowerCase()) ||
  //       pedido.shipping_address_1
  //         .toLowerCase()
  //         .includes(searchTerm.toLowerCase())
  //   );

  //   if (firstMatchIndex !== -1 && cardRefs.current[firstMatchIndex]) {
  //     cardRefs.current[firstMatchIndex].scrollIntoView({
  //       behavior: "smooth",
  //       block: "center",
  //     });
  //   }
  // }, [searchTerm, pedidosLocal]);

  const pedidosFiltrados = pedidosLocal.filter((pedido) => {
    const texto = searchTerm.toLowerCase();
    return (
      // pedido.shipping_first_name?.toLowerCase().includes(texto) ||
      // pedido.shipping_last_name?.toLowerCase().includes(texto) ||
      // pedido.shipping_address_1?.toLowerCase().includes(texto) ||
      pedido.identifier?.toLowerCase().includes(texto)
    );
  });

  return (
    <>
      {showSearch && (
        <div
          ref={searchRef}
          className="position-fixed start-50 translate-middle-x mb-3 p-2 bg-white border rounded shadow d-flex align-items-center"
          style={{
            bottom: "7rem",
            width: "90%",
            maxWidth: "600px",
            zIndex: 3000,
          }}
        >
          <input
            type="text"
            className="form-control me-2"
            placeholder="Buscar pedido..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setSearchTerm("")}
              style={{ whiteSpace: "nowrap" }}
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
      )}

      <BotonesFlotantes setShowSearch={setShowSearch} />

      {!loadingPedidos && (
        <RutaProgreso
          total={totalPedidos}
          entregados={totalPedidosENT}
          noEntregados={totalPedidosNEN}
        />
      )}

      {errorPedidos && <Alert variant={"danger"} />}

      {loadingPedidos && (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner />
        </div>
      )}

      {!loadingPedidos && !errorPedidos && pedidosFiltrados && (
        <ul className="lm-0 pl-0">
          {pedidosFiltrados.map((pedido, i) => (
            <div
              key={i}
              ref={(el) => (cardRefs.current[i] = el)}
              id={`pedido-${i}`}
            >
              <PedidoCard
                pedido={pedido}
                cardIndex={i}
                total={pedidosFiltrados.length}
                scrollToIndex={(targetIndex) => {
                  const ref = cardRefs.current[targetIndex];
                  if (ref) {
                    ref.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                }}
                onEntregaClick={handleEntrega}
                onNoEntregaClick={handleNoEntrega}
                onVerEntregaClick={handleVerEntrega}
                editable={editable}
              />
            </div>
          ))}
        </ul>
      )}

      {showFormularioModalNEN && (
        <ModalNoEntregado
          show={showFormularioModalNEN}
          pedido={pedidoSeleccionado}
          onHide={() => {
            resetValues();
            setShowFormularioModalNEN(false);
          }}
          wsgstatus={wsgstatus}
          subiendo={subiendo}
          setSubiendo={setSubiendo}
        />
      )}

      {showFormularioModalENT && (
        <ModalEntregado
          show={showFormularioModalENT}
          pedido={pedidoSeleccionado}
          onHide={() => {
            resetValues();
            setShowFormularioModalENT(false);
          }}
          wsgstatus={wsgstatus}
          subiendo={subiendo}
          setSubiendo={setSubiendo}
        />
      )}

      {showVerEntregaModal && (
        <ModalVerEntrega
          show={showVerEntregaModal}
          pedido={pedidoSeleccionado}
          onHide={() => {
            setShowVerEntregaModal(false);
          }}
        />
      )}
    </>
  );
};

export default ListaPedidos;
